import { useState, useEffect, useMemo, useCallback } from "react";
import { modules, entities } from "byzantine";
import filters from "byzantine/src/filters";
import {
  getReadableTimeFromCutoff,
  getIsPastACHCutoff,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useLocalization } from "@fluent/react";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import calculateInstantOrSameDayFee from "../calculateInstantOrSameDayFee";

export type DeliverySpeedOption = {
  value: "standard" | "sameDay" | "instant";
  label: string;
  arrival: string;
  fee: string;
  badgeLabel?: string;
  expedite: boolean;
  expediteIcon?: "clock" | "zap";
};

export type DeliverySpeedOptionsResult = {
  options: DeliverySpeedOption[];
  selectedDeliverySpeed: string;
  setDeliverySpeed: (value: string) => void;
  isResolvingOptions: boolean;
  instantFee: API.InstantFeesResponse | null;
  isPastCutoff: boolean;
  cutoffTime: string;
};

/**
 * Owns all delivery-speed business logic: FedNow eligibility, same-day/instant
 * fee fetching, cut-off handling, per-option feature gating, and keeping the
 * form's `deliverySpeed` value valid
 */
const useDeliverySpeedOptions = (): DeliverySpeedOptionsResult => {
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const { setFieldValue } = form;
  const recipient = entities.recipients.useSelectedRecipient();
  const isDebitPayment = modules.achPayments.useIsDebitACHPayment();
  const isSingleExpediteEnabled =
    modules.achPayments.useIsSingleExpediteEnabled();
  const institution = useInstitutionSettings();
  const cutoffTime = getReadableTimeFromCutoff(institution.ach_next_cutoff);
  const isPastCutoff = getIsPastACHCutoff(
    institution.ach_next_cutoff,
    institution.time_zone
  );
  const hasSameDayOption =
    Boolean(features.same_day_ach) && isSingleExpediteEnabled;
  const hasInstantFeature =
    Boolean(features.fednow_send) && isSingleExpediteEnabled;
  const routingNumber = recipient?.ach_destination?.routing_number;
  const selectedDeliverySpeed = form.values.deliverySpeed;
  const { checkReceiveInstantEligibility } =
    modules.transfers.useCheckReceiveInstantEligibility();
  const { getInstantOrSameDayFees } =
    modules.transfers.useGetInstantOrSameDayFees();
  const [sameDayFee, setSameDayFee] = useState<API.InstantFeesResponse | null>(
    null
  );
  const [instantFee, setInstantFee] = useState<API.InstantFeesResponse | null>(
    null
  );
  const [isCheckingInstantEligibility, setIsCheckingInstantEligibility] =
    useState(!isDebitPayment && hasInstantFeature && Boolean(routingNumber));
  const [isInstantEligible, setIsInstantEligible] = useState(false);

  useEffect(() => {
    if (!hasSameDayOption) {
      setSameDayFee(null);
      return;
    }

    getInstantOrSameDayFees("sameDay")
      .then((data) => setSameDayFee(data))
      .catch(() => setSameDayFee(null));
  }, [getInstantOrSameDayFees, hasSameDayOption]);

  useEffect(() => {
    if (isDebitPayment || !hasInstantFeature) {
      setInstantFee(null);
      return;
    }

    getInstantOrSameDayFees("instant")
      .then((data) => setInstantFee(data))
      .catch(() => setInstantFee(null));
  }, [getInstantOrSameDayFees, hasInstantFeature, isDebitPayment]);

  useEffect(() => {
    if (isDebitPayment || !hasInstantFeature || !routingNumber) {
      setIsCheckingInstantEligibility(false);
      setIsInstantEligible(false);
      return;
    }

    setIsCheckingInstantEligibility(true);

    checkReceiveInstantEligibility(routingNumber)
      .then((eligible) => setIsInstantEligible(eligible))
      .catch(() => setIsInstantEligible(false))
      .finally(() => setIsCheckingInstantEligibility(false));
  }, [
    checkReceiveInstantEligibility,
    hasInstantFeature,
    isDebitPayment,
    routingNumber,
  ]);

  const sameDayFeeAmount = calculateInstantOrSameDayFee(
    form.values.amount || (0 as Cents),
    sameDayFee
  );
  const instantFeeAmount = calculateInstantOrSameDayFee(
    form.values.amount || (0 as Cents),
    instantFee
  );
  const hasInstantOption =
    hasInstantFeature && !isDebitPayment && isInstantEligible;

  const options = useMemo<DeliverySpeedOption[]>(
    () => [
      {
        value: "standard",
        label: `${l10n.getString("label-standard")} ACH`,
        arrival: l10n.getString("label-standard-delivery-speed-arrival"),
        fee: l10n.getString("label-standard-delivery-speed-fee"),
        expedite: false,
      },
      ...(hasSameDayOption
        ? [
            {
              value: "sameDay" as const,
              label: l10n.getString("label-same-day"),
              arrival: isPastCutoff
                ? l10n.getString(
                    "label-same-day-delivery-speed-arrival-next-business-day"
                  )
                : l10n.getString("label-same-day-delivery-speed-arrival"),
              fee:
                sameDayFeeAmount > 0
                  ? l10n.getString("label-instant-delivery-speed-fee", {
                      hasFee: "true",
                      fee: filters.currency(sameDayFeeAmount as Dollars, {
                        hasDecimal: true,
                      }),
                    })
                  : l10n.getString("label-no-fee"),
              expedite: true,
              expediteIcon: "clock" as const,
            },
          ]
        : []),
      ...(hasInstantOption
        ? [
            {
              value: "instant" as const,
              label: l10n.getString("label-instant"),
              arrival: l10n.getString("label-instant-delivery-speed-arrival"),
              fee: l10n.getString("label-instant-delivery-speed-fee", {
                hasFee: instantFeeAmount > 0 ? "true" : "false",
                fee: filters.currency(instantFeeAmount as Dollars, {
                  hasDecimal: true,
                }),
              }),
              expedite: true,
              expediteIcon: "zap" as const,
            },
          ]
        : []),
    ],
    [
      hasInstantOption,
      hasSameDayOption,
      instantFeeAmount,
      isPastCutoff,
      l10n,
      sameDayFeeAmount,
    ]
  );

  const shouldWaitForInstantEligibility =
    selectedDeliverySpeed === "instant" &&
    !isDebitPayment &&
    hasInstantFeature &&
    (!recipient || (Boolean(routingNumber) && isCheckingInstantEligibility));

  // true while an instant-capable payment is still awaiting the FedNow eligibility result
  const isResolvingOptions = Boolean(
    !isDebitPayment &&
      hasInstantFeature &&
      routingNumber &&
      isCheckingInstantEligibility
  );

  // called for both an explicit card selection and the invoice flow's automatic recommendation
  const setDeliverySpeed = useCallback(
    (value: string) => {
      setFieldValue("deliverySpeed", value);
      setFieldValue("deliverySpeedManuallySelected", true);
    },
    [setFieldValue]
  );

  // keep the form's deliverySpeed valid: if the selected option is no longer
  // offered (e.g. recipient became ineligible for instant), clear it and let
  // the recommendation effect pick again
  useEffect(() => {
    if (shouldWaitForInstantEligibility) {
      return;
    }

    const allowedValues = options.map((option) => option.value);

    if (!allowedValues.includes(selectedDeliverySpeed as never)) {
      setFieldValue("deliverySpeed", "");
      setFieldValue("deliverySpeedManuallySelected", false);
    }
  }, [
    options,
    selectedDeliverySpeed,
    setFieldValue,
    shouldWaitForInstantEligibility,
  ]);

  return {
    options,
    selectedDeliverySpeed,
    setDeliverySpeed,
    isResolvingOptions,
    instantFee,
    isPastCutoff,
    cutoffTime,
  };
};

export default useDeliverySpeedOptions;
